/**
 * ConfirmDialog - Custom confirmation dialog component
 *
 * Replaces browser's native window.confirm() to provide:
 * - Custom branding (shows "File Uploader" instead of domain)
 * - Better styling and UX
 * - Consistent appearance across browsers
 */

import React from "react";
import styled from "styled-components";
import { Button as LatitudeButton } from "@flexport/latitude-typescript";

/**
 * Modal overlay that covers the entire screen
 */
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

/**
 * Dialog container with white background and shadow
 */
const Dialog = styled.div`
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
  min-width: 400px;
  max-width: 500px;
  padding: 0;
`;

/**
 * Dialog header with title
 */
const DialogHeader = styled.div`
  padding: 16px 24px;
  border-bottom: 1px solid #e5e5e5;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

/**
 * Dialog body with message content
 */
const DialogBody = styled.div`
  padding: 24px;
  font-size: 14px;
  line-height: 1.6;
  color: #555;
  white-space: pre-line; /* Preserve line breaks from \n */
`;

/**
 * Dialog footer with action buttons
 */
const DialogFooter = styled.div`
  padding: 16px 24px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  border-top: 1px solid #e5e5e5;
`;

/**
 * Props for ConfirmDialog component
 */
interface ConfirmDialogProps {
  /** Whether the dialog is visible */
  isOpen: boolean;
  /** Dialog title (e.g., "File Uploader") */
  title?: string;
  /** Message to display in the dialog body */
  message: string;
  /** Text for the confirm button (default: "OK") */
  confirmText?: string;
  /** Text for the cancel button (default: "Cancel") */
  cancelText?: string;
  /** Callback when user confirms */
  onConfirm: () => void;
  /** Callback when user cancels */
  onCancel: () => void;
}

/**
 * Custom confirmation dialog component
 *
 * Usage:
 * ```tsx
 * const [showDialog, setShowDialog] = useState(false);
 *
 * <ConfirmDialog
 *   isOpen={showDialog}
 *   title="File Uploader"
 *   message="Are you sure you want to delete this file?"
 *   onConfirm={() => { handleDelete(); setShowDialog(false); }}
 *   onCancel={() => setShowDialog(false)}
 * />
 * ```
 */
const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title = "File Uploader",
  message,
  confirmText = "OK",
  cancelText = "Cancel",
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  // Handle clicks on overlay (close dialog)
  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  };

  // Handle keyboard events (Escape to cancel, Enter to confirm)
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      onCancel();
    } else if (e.key === "Enter") {
      onConfirm();
    }
  };

  return (
    <Overlay onClick={handleOverlayClick} onKeyDown={handleKeyDown}>
      <Dialog role="dialog" aria-modal="true" aria-labelledby="dialog-title">
        <DialogHeader id="dialog-title">{title}</DialogHeader>
        <DialogBody>{message}</DialogBody>
        <DialogFooter>
          <LatitudeButton
            label={cancelText}
            intent="basic"
            kind="hollow"
            size="m"
            isLoading={false}
            width="responsive"
            type="button"
            disabled={false}
            onClick={onCancel}
            data-testid="confirm-dialog-cancel"
          >
            {cancelText}
          </LatitudeButton>
          <LatitudeButton
            label={confirmText}
            intent="danger"
            kind="solid"
            size="m"
            isLoading={false}
            width="responsive"
            type="button"
            disabled={false}
            onClick={onConfirm}
            data-testid="confirm-dialog-confirm"
          >
            {confirmText}
          </LatitudeButton>
        </DialogFooter>
      </Dialog>
    </Overlay>
  );
};

export default ConfirmDialog;
